import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Navigation, Search, X, Loader2, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import PredictionsDropdown from './location/PredictionsDropdown';
import { parsePlace, buildLocationFromGeocode } from './location/locationParsing';

const DISTANCE_OPTIONS = [
  { value: 5, label: '5 km' },
  { value: 10, label: '10 km' },
  { value: 25, label: '25 km' },
  { value: 50, label: '50 km' },
  { value: 100, label: '100 km' },
  { value: 0, label: 'Any distance' }
];

/**
 * Smart Location Search Box - Realtor.ca Style
 * Used in HomePage hero section
 */
const LocationSearchBox = ({
  onLocationChange,
  onDistanceChange,
  onSearch,
  defaultDistance = 25,
  showDistanceFilter = true,
  compact = false,
  placeholder = "Search by city, neighborhood, or address..."
}) => {
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const [inputValue, setInputValue] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [distance, setDistance] = useState(defaultDistance);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showDistanceDropdown, setShowDistanceDropdown] = useState(false);
  const [predictions, setPredictions] = useState({ locations: [], addresses: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [showPredictions, setShowPredictions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Load Google Maps Script
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;

    if (window.google?.maps?.places) {
      setIsScriptLoaded(true);
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      const checkLoaded = setInterval(() => {
        if (window.google?.maps?.places) {
          setIsScriptLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);
      setTimeout(() => clearInterval(checkLoaded), 10000);
      return () => clearInterval(checkLoaded);
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => setIsScriptLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Initialize services
  useEffect(() => {
    if (!isScriptLoaded) return;
    
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    const dummyDiv = document.createElement('div');
    placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
  }, [isScriptLoaded]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowPredictions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch predictions
  const fetchPredictions = useCallback((query) => {
    if (!autocompleteServiceRef.current || query.length < 2) {
      setPredictions({ locations: [], addresses: [] });
      return;
    }

    setIsLoading(true);

    const locationRequest = {
      input: query,
      componentRestrictions: { country: 'ca' },
      types: ['(regions)']
    };

    const addressRequest = {
      input: query,
      componentRestrictions: { country: 'ca' },
      types: ['address']
    };

    Promise.all([
      new Promise((resolve) => {
        autocompleteServiceRef.current.getPlacePredictions(locationRequest, (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            resolve(results.slice(0, 4));
          } else {
            resolve([]);
          }
        });
      }),
      new Promise((resolve) => {
        autocompleteServiceRef.current.getPlacePredictions(addressRequest, (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            resolve(results.slice(0, 3));
          } else {
            resolve([]);
          }
        });
      })
    ]).then(([locations, addresses]) => {
      setPredictions({ locations, addresses });
      setIsLoading(false);
      setHighlightedIndex(-1);
    }).catch(() => {
      setIsLoading(false);
    });
  }, []);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowPredictions(true);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchPredictions(value);
    }, 300);
  };

  // Select prediction
  const handleSelectPrediction = (prediction) => {
    if (!placesServiceRef.current) return;

    setIsLoading(true);
    setShowPredictions(false);
    setInputValue(prediction.description);

    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['geometry', 'formatted_address', 'address_components', 'name', 'types']
      },
      (place, status) => {
        setIsLoading(false);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
          const location = parsePlace(place, prediction);
          setSelectedLocation(location);
          onLocationChange?.(location);
        }
      }
    );
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    const allPredictions = [...predictions.locations, ...predictions.addresses];
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => prev < allPredictions.length - 1 ? prev + 1 : prev);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && allPredictions[highlightedIndex]) {
        handleSelectPrediction(allPredictions[highlightedIndex]);
      } else if (selectedLocation) {
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      setShowPredictions(false);
    }
  };

  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported');
      return;
    }

    setGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`
          );
          const data = await response.json();
          
          if (data.results?.[0]) {
            const location = buildLocationFromGeocode(data.results[0], latitude, longitude);
            
            setInputValue('📍 Current Location');
            setSelectedLocation(location);
            setShowPredictions(false);
            onLocationChange?.(location);
          }
        } catch (error) {
          console.error('Error:', error);
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        setGettingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          alert('Please enable location access');
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleDistanceChange = (value) => {
    setDistance(value);
    setShowDistanceDropdown(false);
    onDistanceChange?.(value);
  };

  const handleSearch = () => {
    onSearch?.({
      location: selectedLocation,
      distance: distance
    });
  };

  const clearLocation = () => {
    setInputValue('');
    setSelectedLocation(null);
    setPredictions({ locations: [], addresses: [] });
    setShowPredictions(false);
    onLocationChange?.(null);
    inputRef.current?.focus();
  };

  const allPredictions = [...predictions.locations, ...predictions.addresses];
  const hasResults = allPredictions.length > 0;

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => inputValue.length >= 2 && setShowPredictions(true)}
            placeholder={placeholder}
            className="w-full pl-9 pr-8 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-800"
          />
          {inputValue && (
            <button onClick={clearLocation} className="absolute right-2 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={getCurrentLocation}
          disabled={gettingLocation}
        >
          {gettingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 space-y-4">
      {/* Smart Location Input */}
      <div className="relative">
        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500 z-10" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue.length >= 2 && setShowPredictions(true)}
          placeholder={placeholder}
          disabled={!isScriptLoaded}
          className="w-full pl-12 pr-24 py-3.5 text-base border-2 border-gray-200 dark:border-gray-600 rounded-xl 
            focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-800
            placeholder:text-gray-400"
        />
        
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isLoading && <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />}
          {inputValue && !isLoading && (
            <button onClick={clearLocation} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
          <button
            onClick={getCurrentLocation}
            disabled={gettingLocation}
            className="p-2 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-lg text-red-500"
            title="Use my location"
          >
            {gettingLocation ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Navigation className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Predictions Dropdown */}
        {showPredictions && (inputValue.length >= 2 || hasResults) && (
          <PredictionsDropdown
            dropdownRef={dropdownRef}
            isLoading={isLoading}
            hasResults={hasResults}
            inputValue={inputValue}
            predictions={predictions}
            highlightedIndex={highlightedIndex}
            onSelect={handleSelectPrediction}
          />
        )}
      </div>

      {/* Distance Filter & Search */}
      <div className="flex items-center gap-3">
        {showDistanceFilter && (
          <div className="relative">
            <button
              onClick={() => setShowDistanceDropdown(!showDistanceDropdown)}
              className="flex items-center gap-2 px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-gray-300 bg-white dark:bg-gray-800"
            >
              <span className="text-sm font-medium">
                {distance === 0 ? 'Any distance' : `Within ${distance} km`}
              </span>
              <ChevronDown className="w-4 h-4" />
            </button>
            
            {showDistanceDropdown && (
              <div className="absolute top-full left-0 mt-1 w-40 bg-white dark:bg-gray-800 border rounded-xl shadow-lg z-50 overflow-hidden">
                {DISTANCE_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleDistanceChange(option.value)}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      distance === option.value ? 'bg-red-50 text-red-600' : ''
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        
        <Button
          onClick={handleSearch}
          disabled={!selectedLocation}
          className="flex-1 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2.5 rounded-xl"
        >
          <Search className="w-4 h-4 mr-2" />
          Search Trainers
        </Button>
      </div>

      {/* Hint */}
      {!selectedLocation && (
        <p className="text-xs text-gray-500 text-center">
          💡 Start typing to see suggestions, or click <Navigation className="w-3 h-3 inline" /> to use your location
        </p>
      )}
    </div>
  );
};

export default LocationSearchBox;
